import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  BadgeCheck,
  Calendar,
  Check,
  Clock,
  FileText,
  Minus,
  Plus,
  RefreshCw,
  Shield,
  TrendingDown,
  TrendingUp,
  UserPlus,
  Users,
} from "@ews-admin/global-design-system";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PaymentInProgressOverlay } from "../../components/molecules/PaymentInProgressOverlay/PaymentInProgressOverlay";
import { TabNavigation } from "../../components/molecules/TabNavigation/TabNavigation";
import { useFacilityUser } from "../../hooks/useFacilityUser";
import { facilitiesApi } from "../../services/facilities.api";
import { licenceApi } from "../../services/licence.api";
import { paymentApi } from "../../services/payment.api";
import { promotionApi } from "../../services/promotion.api";
import { searchApi } from "../../services/search.api";
import { fetchLicenceSuccess, selectLicenceState } from "../../store/reducers/licenceSlice";
import { generateUUID } from "../../utils/date.utils";
import { applyDurationDiscount, findDurationDiscountPercentage } from "../../utils/duration-discount.utils";
import { formatMoney } from "../../utils/format.utils";
import { formatBillingPeriod, formatDateOnly } from "../../utils/invoice.utils";
import { applyPromotion, priceCycles, toLocalDateOnly } from "../../utils/promotion.utils";
import {
  ContractDuration,
  DurationDiscount,
  LicencePlan,
  LicenceStatus,
  PaymentBillingMode,
} from "../../types/licence.definition";
import {
  InvoiceSummary,
  OpenPaymentIntent,
  PAYMENT_SESSION_KEY,
  PaymentSessionContext,
} from "../../types/payment";
import { ActivePromotionResponse } from "../../types/promotion.definition";
import { defaultSearchRequest, Order } from "../../types/search.definition";
import { InvoicesTab } from "./licence/InvoicesTab";

// Same UTC-midnight pitfall applies to date-only arithmetic/comparisons (trial
// countdowns, "locked until" checks, pro-ration day counts) — parse the Y/M/D
// components into a local-midnight Date instead of letting `new Date(str)`
// silently reinterpret it in UTC.
function parseDateOnly(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function remainingDays(endDate: string): number {
  const end = parseDateOnly(endDate);
  const now = new Date();
  if (end <= now) return 0;
  return Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

export function remainingMonths(endDate: string): number {
  const end = parseDateOnly(endDate);
  const now = new Date();
  if (end <= now) return 0;
  const months =
    (end.getFullYear() - now.getFullYear()) * 12 +
    (end.getMonth() - now.getMonth()) +
    (end.getDate() > now.getDate() ? 1 : 0);
  return Math.max(0, months);
}

function canRenew(contractDuration: string, endDate: string): boolean {
  const days = remainingDays(endDate);
  const months = remainingMonths(endDate);
  switch (contractDuration) {
    case "ONE_MONTH":
      return days <= 15;
    case "ONE_YEAR":
      return months <= 3;
    case "THREE_YEARS":
    case "FIVE_YEARS":
      return months <= 6;
    default:
      return false;
  }
}

const CONTRACT_DURATIONS: ContractDuration[] = ["ONE_MONTH", "ONE_YEAR", "THREE_YEARS", "FIVE_YEARS"];

const PLAN_ORDER = ["DEBUTANT", "PROFESSIONNEL", "PREMIUM"];

// MED-1321: the invoice's unfinished checkout, if any. A failed lookup counts as
// "none" — initiate reuses an open payment server-side, so it must not block paying.
async function findOpenPayment(invoiceId: number): Promise<OpenPaymentIntent | null> {
  try {
    return (await paymentApi.getOpenInvoicePayment(invoiceId)) ?? null;
  } catch {
    return null;
  }
}

type ActivePanel = "changePlan" | "renew" | "addDoctorNow" | null;

// MED-1652: the page's tab lives in the URL (?tab=invoices) so a link can open it.
export const INVOICES_TAB = "invoices";
type LicenceTab = "licence" | typeof INVOICES_TAB;

export const LicencePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const fmt = (amount: number | undefined) => formatMoney(amount, i18n.language);
  const formatDate = (dateStr: string | null | undefined) => formatDateOnly(dateStr, i18n.language);
  const facilityUser = useFacilityUser();
  const facilityId = facilityUser?.healthcareFacility?.id;
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: LicenceTab = searchParams.get("tab") === INVOICES_TAB ? INVOICES_TAB : "licence";
  const handleTabChange = (tabId: string) => {
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        if (tabId === INVOICES_TAB) next.set("tab", INVOICES_TAB);
        else next.delete("tab");
        return next;
      },
      { replace: true }
    );
  };

  const { licence, loading, error, errorCode } = useSelector(selectLicenceState);
  const noLicenceYet = errorCode === "LICENCE_NOT_FOUND";
  // The plan picker is only for facilities with NO licence at all. Managers are
  // onboarded by an admin who already sets up their licence, so a first-time
  // manager already HAS a licence (ACTIVE, never paid) — they don't choose one,
  // they just pay. Those managers fall through to the licence card + the pending
  // invoice Pay card below; the welcoming "make your first payment" banner in the
  // dashboard layout sends them here.
  const showOnboarding = noLicenceYet;

  const [activePanel, setActivePanel] = useState<ActivePanel>(null);
  const panelSectionRef = useRef<HTMLDivElement>(null);

  // MED-1759: the change-plan and add-doctors sections take the pending invoice's place,
  // and come into view when they open.
  const panelReplacesInvoice = activePanel === "changePlan" || activePanel === "addDoctorNow";
  useEffect(() => {
    if (panelReplacesInvoice) {
      panelSectionRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }
  }, [panelReplacesInvoice, activePanel]);

  // MED-1172: active facility promotion (e.g. free trial month, rebate)
  const [activePromotion, setActivePromotion] = useState<ActivePromotionResponse | null>(null);

  // Payment state
  const [pendingInvoice, setPendingInvoice] = useState<InvoiceSummary | null>(null);
  const [paymentInitiating, setPaymentInitiating] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  // MED-1321: while set, the non-dismissible "Payment in progress" overlay is shown
  // and no new checkout can be started until that one is resumed or cancelled.
  const [openPayment, setOpenPayment] = useState<{ invoiceId: number; intent: OpenPaymentIntent } | null>(null);
  const [plans, setPlans] = useState<LicencePlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);

  // Change-plan state
  const [selectedPlan, setSelectedPlan] = useState<LicencePlan | null>(null);
  const [doctorCount, setDoctorCount] = useState(1);

  // Renew state
  const [selectedDuration, setSelectedDuration] = useState<ContractDuration>("ONE_YEAR");

  // Yearly billing-mode state (MED-1200) — only meaningful when selectedDuration === "ONE_YEAR".
  // Shared by the onboarding (create) panel and the renew panel.
  const [selectedBillingMode, setSelectedBillingMode] = useState<PaymentBillingMode | null>("MONTHLY");
  // Separate billing-mode selection for the pending-invoice "just pay" card: a manager whose
  // facility was onboarded directly by an admin already has an ACTIVE ONE_YEAR licence with no
  // billing mode chosen yet, and never goes through the onboarding/renew panels to choose one.
  const [pendingInvoiceBillingMode, setPendingInvoiceBillingMode] = useState<PaymentBillingMode>("MONTHLY");
  // Discount percentages per contract duration / billing mode, as the server bills them.
  const [durationDiscounts, setDurationDiscounts] = useState<DurationDiscount[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Add doctors now state
  const [addDoctorNowCount, setAddDoctorNowCount] = useState(1);

  // Doctor removal state
  const [currentDoctorCount, setCurrentDoctorCount] = useState(0);
  const [facilityDoctors, setFacilityDoctors] = useState<Array<{ id: number; firstName: string; lastName: string; email: string }>>([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [selectedDoctorsToRemove, setSelectedDoctorsToRemove] = useState<number[]>([]);

  // Secretary removal state
  const [currentSecretaryCount, setCurrentSecretaryCount] = useState(0);
  const [facilitySecretaries, setFacilitySecretaries] = useState<Array<{ id: number; firstName: string; lastName: string; email: string }>>([]);
  const [secretariesLoading, setSecretariesLoading] = useState(false);
  const [selectedSecretariesToRemove, setSelectedSecretariesToRemove] = useState<number[]>([]);

  useEffect(() => {
    if (!facilityId) return;
    facilitiesApi.getFacilityStaffStats(Number(facilityId))
      .then((stats) => {
        // MED-1757: the quota counts active staff, like the API.
        setCurrentDoctorCount(stats?.summary?.metrics?.doctors?.active ?? 0);
        setCurrentSecretaryCount(stats?.summary?.metrics?.secretaries?.active ?? 0);
      })
      .catch(() => {});
  }, [facilityId]);

  // MED-1757: a first licence must cover the staff already in place.
  const minDoctorCount = Math.max(1, currentDoctorCount);
  useEffect(() => {
    if (!showOnboarding) return;
    setDoctorCount((n) => Math.max(n, currentDoctorCount));
    setSelectedPlan((plan) => (plan && plan.maxSecretaries < currentSecretaryCount ? null : plan));
  }, [showOnboarding, currentDoctorCount, currentSecretaryCount]);

  // Loads the pending invoice and, if it has an unfinished checkout, shows the
  // "Payment in progress" overlay for it (MED-1321).
  const loadPendingInvoice = useCallback(async () => {
    if (!facilityId) return;
    let invoice: InvoiceSummary | null;
    try {
      invoice = await paymentApi.getPendingInvoice(Number(facilityId));
    } catch {
      invoice = null;
    }
    setPendingInvoice(invoice);
    if (!invoice) return;
    const intent = await findOpenPayment(invoice.id);
    setOpenPayment(intent ? { invoiceId: invoice.id, intent } : null);
  }, [facilityId]);

  useEffect(() => {
    void loadPendingInvoice();
  }, [loadPendingInvoice]);

  // MED-1321: going Back from PayDunya can restore this page from the bfcache with
  // its pre-redirect state (Pay spinner still on) — reset it and re-check the invoice.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      setSubmitting(false);
      setPaymentInitiating(false);
      void loadPendingInvoice();
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, [loadPendingInvoice]);

  // MED-1172: fetch the facility's active promotion, if any.
  useEffect(() => {
    if (!facilityId) return;
    promotionApi.getActivePromotions(Number(facilityId))
      .then((promotions) => setActivePromotion(promotions[0] ?? null))
      .catch(() => {});
    // Refetch on language change: the promotion label is localised by the BFF.
  }, [facilityId, i18n.language]);

  // Fetch the duration discounts (incl. the admin-configured yearly billing-mode ones) once.
  useEffect(() => {
    licenceApi.getDurationDiscounts().then(setDurationDiscounts);
  }, []);

  // Backend rejects a non-null paymentBillingMode for any duration other than ONE_YEAR.
  useEffect(() => {
    if (selectedDuration !== "ONE_YEAR") setSelectedBillingMode(null);
  }, [selectedDuration]);

  // Load available plans so a manager who is still onboarding (no licence yet,
  // or a never-paid PENDING_PAYMENT licence) can pick one, or so the pending-invoice
  // billing-mode price breakdown can show the licence-type/doctor-price split (MED-1194).
  useEffect(() => {
    if ((!showOnboarding && !pendingInvoice) || plans.length > 0) return;
    setPlansLoading(true);
    licenceApi.getAvailablePlans()
      .then(setPlans)
      .finally(() => setPlansLoading(false));
  }, [showOnboarding, pendingInvoice, plans.length]);

  // Re-reads the licence without the store's loading flag (which would swap this page
  // for DashboardLayout's full-screen loader), then the pending invoice.
  const refreshLicenceAndInvoice = async () => {
    if (!facilityId) return;
    try {
      dispatch(fetchLicenceSuccess(await licenceApi.getLicenceByFacility(Number(facilityId))));
    } catch {
      // Non-fatal: the licence card keeps its last known state.
    }
    await loadPendingInvoice();
  };

  // Shows the "Payment in progress" overlay when the invoice already has an unfinished checkout.
  const showOpenPaymentIfAny = async (invoiceId: number): Promise<boolean> => {
    const intent = await findOpenPayment(invoiceId);
    if (!intent) return false;
    setOpenPayment({ invoiceId, intent });
    return true;
  };

  const handleOpenPaymentResolved = () => {
    setOpenPayment(null);
    void refreshLicenceAndInvoice();
  };

  // Shared by handleCreateLicence (first-time purchase) and handlePayInvoice (later/overdue
  // payments) — initiates checkout for a given invoice and redirects to PayDunya. Resolves
  // true when it redirects (the caller keeps its spinner until the page unloads), false when
  // no checkout was started: the invoice already had one open (the overlay is shown instead),
  // or initiate reused a payment that turned out to be already paid (MED-1321).
  const payForInvoice = async (invoiceId: number): Promise<boolean> => {
    if (await showOpenPaymentIfAny(invoiceId)) return false;
    const idempotencyKey = generateUUID();
    const result = await paymentApi.initiateInvoicePayment(invoiceId, idempotencyKey);
    if (result.status === "COMPLETED") {
      toast.success(t("payment.callback.success.title"));
      await refreshLicenceAndInvoice();
      return false;
    }
    if (!result.checkoutUrl) throw new Error("Payment initiated without a checkout URL");
    const ctx: PaymentSessionContext = {
      type: "facility",
      invoiceId,
      paymentIntentId: result.paymentIntentId,
    };
    sessionStorage.setItem(PAYMENT_SESSION_KEY, JSON.stringify(ctx));
    window.location.href = result.checkoutUrl;
    return true;
  };

  // Creates the facility's licence (PENDING_PAYMENT until paid — mirrors the doctor
  // self-serve flow) and immediately continues into checkout for its first invoice.
  const licenceLimitMessage = (err: unknown): string | null => {
    const code = (err as { code?: string })?.code;
    return code === "DOCTOR_LICENCE_LIMIT_REACHED" || code === "SECRETARY_LICENCE_LIMIT_REACHED"
      ? t(`error.${code}.description`)
      : null;
  };

  const handleCreateLicence = async () => {
    if (!selectedPlan || !facilityId || openPayment) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const created = await licenceApi.createLicence(Number(facilityId), {
        licenceType: selectedPlan.type,
        numberOfDoctors: doctorCount,
        contractDuration: selectedDuration,
        healthcareFacilityId: Number(facilityId),
        paymentBillingMode: selectedDuration === "ONE_YEAR" ? selectedBillingMode : null,
      });
      dispatch(fetchLicenceSuccess(created));
      const invoice = await paymentApi.getPendingInvoice(Number(facilityId));
      if (!(await payForInvoice(invoice.id))) setSubmitting(false);
    } catch (err: unknown) {
      setActionError(licenceLimitMessage(err) ?? t("licence.create.error"));
      setSubmitting(false);
    }
  };

  // MED-1200: whenever a ONE_YEAR licence has an unpaid pending invoice, the manager
  // must be able to choose (and keep changing their mind about) full-upfront vs.
  // monthly-commitment right up until they actually pay — e.g. a facility onboarded
  // directly by an admin never went through the create/renew panels to choose one, or
  // a manager picked one, got redirected to PayDunya, and hit the browser back button
  // before completing payment. Gating this on "paymentBillingMode is still unset" only
  // showed the choice the very first time — once picked (even if payment was never
  // completed) it silently disappeared.
  const canChooseBillingMode =
    licence?.contractDuration === "ONE_YEAR" && !!pendingInvoice;

  // Reflect the licence's last saved choice (if any) instead of always resetting to the
  // "MONTHLY" default, so returning to this page after backing out of PayDunya shows
  // what was actually picked. The BFF's LicenceResponse is @JsonInclude(NON_NULL), so an
  // unset paymentBillingMode is omitted from the JSON entirely (undefined, not null).
  useEffect(() => {
    if (licence?.paymentBillingMode) setPendingInvoiceBillingMode(licence.paymentBillingMode);
  }, [licence?.paymentBillingMode]);

  const handlePayInvoice = async () => {
    if (!pendingInvoice || !licence || !facilityId || openPayment) return;
    setPaymentInitiating(true);
    setPaymentError(null);
    try {
      let invoiceId = pendingInvoice.id;
      if (canChooseBillingMode && pendingInvoiceBillingMode !== licence.paymentBillingMode) {
        // Switching the billing mode rewrites or replaces this invoice — never do that under
        // an open checkout for it: that one has to be resumed or cancelled first (MED-1321).
        if (await showOpenPaymentIfAny(invoiceId)) {
          setPaymentInitiating(false);
          return;
        }
        const updated = await licenceApi.updateLicence(Number(facilityId), licence.id, {
          licenceType: licence.licenceType,
          numberOfDoctors: licence.numberOfDoctors,
          contractDuration: licence.contractDuration,
          healthcareFacilityId: Number(facilityId),
          paymentBillingMode: pendingInvoiceBillingMode,
        });
        dispatch(fetchLicenceSuccess(updated));
        // Re-read: choosing full-upfront replaces the monthly pending invoice with a
        // new 12-month lump-sum one; choosing monthly-commitment just resizes it.
        const refreshed = await paymentApi.getPendingInvoice(Number(facilityId));
        invoiceId = refreshed.id;
      }
      if (!(await payForInvoice(invoiceId))) setPaymentInitiating(false);
    } catch {
      setPaymentError(t("payment.initiate.error"));
      setPaymentInitiating(false);
    }
  };

  // MED-1652: "Pay" on a pending/overdue invoice of the Invoices tab — same checkout as above,
  // for that invoice as it stands. Resolves true when it redirects to the checkout.
  const handlePayListedInvoice = async (invoiceId: number): Promise<boolean> => {
    if (openPayment) return false;
    try {
      return await payForInvoice(invoiceId);
    } catch {
      toast.error(t("payment.initiate.error"));
      return false;
    }
  };

  const openPanel = async (panel: ActivePanel) => {
    const next = activePanel === panel ? null : panel;
    setActivePanel(next);
    setActionError(null);

    if (next === "changePlan" && licence) {
      // Load plans if needed
      let loadedPlans = plans;
      if (plans.length === 0) {
        setPlansLoading(true);
        try {
          loadedPlans = await licenceApi.getAvailablePlans();
          setPlans(loadedPlans);
        } finally {
          setPlansLoading(false);
        }
      }
      const current = loadedPlans.find((p) => p.type === licence.licenceType) ?? loadedPlans[0] ?? null;
      setSelectedPlan(current);
      // Initialise doctor count to pending value if a reduction is already in progress
      setDoctorCount(licence.pendingNumberOfDoctors ?? licence.numberOfDoctors);
      // Start with empty selections — API owns the scheduled removals table
      setSelectedDoctorsToRemove([]);
      setSelectedSecretariesToRemove([]);

      // Load doctors and secretaries in one staff search call
      setDoctorsLoading(true);
      setSecretariesLoading(true);
      try {
        const req = { ...defaultSearchRequest, paginationSettings: { limit: 100, offset: 0 }, sortBy: { property: "user.firstName", order: Order.ASC } };
        const resp = await searchApi.searchStaff(req, Number(facilityId));
        const rawDoctors = (resp.doctors ?? []) as Array<{ id: unknown; firstName: string; lastName: string; email: string }>;
        setFacilityDoctors(rawDoctors.map(d => ({ id: Number(d.id), firstName: d.firstName, lastName: d.lastName, email: d.email })));
        const secretaries = (resp.secretaries ?? []) as Array<{ id: unknown; firstName: string; lastName: string; email: string }>;
        setFacilitySecretaries(secretaries.map(s => ({ id: Number(s.id), firstName: s.firstName ?? "", lastName: s.lastName ?? "", email: s.email ?? "" })));
      } catch {
        // Non-critical: staff list is only used for optional removal selection
      } finally {
        setDoctorsLoading(false);
        setSecretariesLoading(false);
      }
    } else if (next && plans.length === 0) {
      setPlansLoading(true);
      try {
        const data = await licenceApi.getAvailablePlans();
        setPlans(data);
      } finally {
        setPlansLoading(false);
      }
    }
  };

  const closePanel = () => {
    setActivePanel(null);
    setActionError(null);
    setSelectedDoctorsToRemove([]);
    setSelectedSecretariesToRemove([]);
  };

  const discountForDuration = (d: ContractDuration, mode?: PaymentBillingMode | null) =>
    findDurationDiscountPercentage(durationDiscounts, d, mode);
  // Duration badges follow the billing mode currently selected for ONE_YEAR, so each
  // badge matches the price summary below it.
  const durationOptions = CONTRACT_DURATIONS.map((value) => ({
    value,
    discount: discountForDuration(value, selectedBillingMode),
  }));

  // MED-1573: a year (annual lump sum, or the "total over the year" of a monthly commitment)
  // is billed month by month, each cycle priced with the promotion active on its due date.
  // The first cycle is due today for a new contract and when a pending invoice is switched to the
  // annual lump sum (the API re-issues it from today); a renewal starts on the next due date.
  const today = toLocalDateOnly(new Date());
  const licenceFirstDueDate = licence?.paymentDueDate ?? today;
  const yearTotal = (monthly: number, firstDueDate: string) => priceCycles(monthly, activePromotion, firstDueDate, 12);

  const effectiveActive = currentDoctorCount > 0 ? currentDoctorCount : (licence?.numberOfDoctors ?? 0);
  const neededDoctorRemovals = Math.max(0, effectiveActive - doctorCount);
  const scheduledDoctorIds = (licence?.scheduledDoctorRemovals ?? []).map(Number).filter(id => id > 0);
  const showDoctorRemoval = neededDoctorRemovals > 0 || scheduledDoctorIds.length > 0;

  const effectiveActiveSecretaries = currentSecretaryCount > 0 ? currentSecretaryCount : 0;
  const neededSecretaryRemovals = selectedPlan ? Math.max(0, effectiveActiveSecretaries - selectedPlan.maxSecretaries) : 0;
  const scheduledSecretaryIds = (licence?.scheduledSecretaryRemovals ?? []).map(Number).filter(id => id > 0);
  const showSecretaryRemoval = neededSecretaryRemovals > 0 || scheduledSecretaryIds.length > 0;

  const handleChangePlan = async () => {
    if (!licence || !selectedPlan || !facilityId) return;

    if (neededDoctorRemovals > 0 && selectedDoctorsToRemove.length < neededDoctorRemovals) {
      setActionError(t("licence.changePlan.selectMoreDoctors", { count: neededDoctorRemovals }));
      return;
    }
    if (neededSecretaryRemovals > 0 && selectedSecretariesToRemove.length < neededSecretaryRemovals) {
      setActionError(t("licence.changePlan.selectMoreSecretaries", { count: neededSecretaryRemovals }));
      return;
    }

    setSubmitting(true);
    setActionError(null);
    try {
      const updated = await licenceApi.updateLicence(Number(facilityId), licence.id, {
        licenceType: selectedPlan.type,
        numberOfDoctors: doctorCount,
        contractDuration: licence.contractDuration,
        healthcareFacilityId: Number(facilityId),
        // Always send the list when the section is visible so backend can cancel stale entries
        ...(showDoctorRemoval && { doctorIdsToRemove: selectedDoctorsToRemove }),
        ...(showSecretaryRemoval && { secretaryIdsToRemove: selectedSecretariesToRemove }),
      });
      dispatch(fetchLicenceSuccess(updated));
      closePanel();
      const hasRemovals = selectedDoctorsToRemove.length > 0 || selectedSecretariesToRemove.length > 0;
      setSuccessMsg(hasRemovals ? t("licence.changePlan.successWithRemovals") : t("licence.changePlan.success"));
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err: unknown) {
      const errCode = (err as { code?: string })?.code;
      if (errCode === "DOCTOR_HAS_UPCOMING_APPOINTMENTS") {
        setActionError(t("licence.changePlan.errorDoctorInUse"));
      } else {
        setActionError(licenceLimitMessage(err) ?? t("licence.changePlan.error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRenew = async () => {
    if (!licence || !facilityId) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const updated = await licenceApi.updateLicence(Number(facilityId), licence.id, {
        licenceType: licence.licenceType,
        numberOfDoctors: licence.numberOfDoctors,
        contractDuration: selectedDuration,
        healthcareFacilityId: Number(facilityId),
        paymentBillingMode: selectedDuration === "ONE_YEAR" ? selectedBillingMode : null,
      });
      dispatch(fetchLicenceSuccess(updated));
      closePanel();
      setSuccessMsg(t("licence.renew.success"));
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch {
      setActionError(t("licence.renew.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddDoctorsNow = async () => {
    if (!licence || !facilityId) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const updated = await licenceApi.addDoctorsNow(Number(facilityId), licence.id, addDoctorNowCount);
      dispatch(fetchLicenceSuccess(updated));
      closePanel();
      setSuccessMsg(t("licence.addDoctorNow.success"));
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch {
      setActionError(t("licence.addDoctorNow.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const contractDurationLabel: Record<string, string> = {
    ONE_MONTH: t("licence.duration.oneMonth"),
    ONE_YEAR: t("licence.duration.oneYear"),
    THREE_YEARS: t("licence.duration.threeYears"),
    FIVE_YEARS: t("licence.duration.fiveYears"),
  };

  // One label and colour per licence status; anything unknown falls back to
  // the "expired" danger badge.
  const statusBadge: Record<LicenceStatus, { label: string; color: string }> = {
    ACTIVE: { label: t("licence.status.active"), color: "bg-success-100 text-success-700" },
    TRIAL: { label: t("licence.status.trial"), color: "bg-blue-100 text-blue-700" },
    PAUSED: { label: t("licence.status.paused"), color: "bg-warning-100 text-warning-700" },
    PENDING_PAYMENT: { label: t("licence.status.pendingPayment"), color: "bg-warning-100 text-warning-700" },
    CANCELLED: { label: t("licence.status.cancelled"), color: "bg-neutral-200 text-neutral-700" },
    EXPIRED: { label: t("licence.status.expired"), color: "bg-danger-100 text-danger-700" },
  };
  const currentStatusBadge = (licence && statusBadge[licence.status]) ?? statusBadge.EXPIRED;

  const currentPlanIndex = licence ? PLAN_ORDER.indexOf(licence.licenceType) : -1;
  const selectedPlanIndex = selectedPlan ? PLAN_ORDER.indexOf(selectedPlan.type) : -1;
  const isPlanChange = selectedPlan && licence && selectedPlan.type !== licence.licenceType;
  const isUpgrade = isPlanChange && selectedPlanIndex > currentPlanIndex;
  const isDowngrade = isPlanChange && selectedPlanIndex < currentPlanIndex;

  const licenceTabContent = (
    <div className="space-y-6">
      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-b-2 rounded-full animate-spin border-primary-600" />
        </div>
      )}

      {!loading && error && !noLicenceYet && (
        <div className="flex items-center gap-3 p-4 border bg-danger-50 border-danger-200 rounded-xl text-danger-700">
          <AlertCircle className="flex-shrink-0 w-5 h-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {!loading && !error && !licence && !noLicenceYet && (
        <div className="flex flex-col items-center justify-center py-24 space-y-3 text-neutral-400">
          <Shield className="w-12 h-12" />
          <p className="text-sm">{t("licence.noLicence")}</p>
        </div>
      )}

      {!loading && showOnboarding && (
        <Panel
          icon={<Shield className="w-5 h-5 text-primary-600" />}
          title={t("licence.create.title")}
          subtitle={t("licence.create.subtitle")}
        >
          {plansLoading ? (
            <Spinner />
          ) : (
            <div className="space-y-6">
              {/* Welcome / onboarding banner — friendly first-time guidance,
                  no payment-overdue framing for a facility that never purchased. */}
              <div className="flex items-start gap-3 p-4 border bg-primary-50 border-primary-200 rounded-xl text-primary-800">
                <Shield className="w-5 h-5 flex-shrink-0 mt-0.5 text-primary-600" />
                <div>
                  <p className="text-sm font-semibold">{t("licence.onboarding.welcomeTitle")}</p>
                  <p className="text-xs mt-0.5 text-primary-700">{t("licence.onboarding.welcomeMessage")}</p>
                </div>
              </div>

              {/* Plan selector */}
              <div>
                <p className="mb-3 text-sm font-medium text-neutral-700">
                  {t("licence.changePlan.selectPlan")}
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {plans.map((plan) => {
                    const planKey = plan.type.toLowerCase().replace("professionnel", "professional");
                    const isSelected = selectedPlan?.type === plan.type;
                    const hasTooFewSecretaries = plan.maxSecretaries < currentSecretaryCount;
                    return (
                      <div
                        key={plan.type}
                        aria-disabled={hasTooFewSecretaries}
                        className={`relative overflow-hidden border rounded-xl p-5 transition-all ${hasTooFewSecretaries ? "border-neutral-200 opacity-60 cursor-not-allowed" : isSelected ? "border-primary-500 bg-primary-50 shadow-md cursor-pointer" : "border-neutral-200 hover:border-primary-300 cursor-pointer"
                          }`}
                        onClick={() => !hasTooFewSecretaries && setSelectedPlan(plan)}
                      >
                        <h3 className="mt-1 font-semibold text-neutral-900">{t(`licence.plans.${planKey}`, plan.type)}</h3>
                        <p className="text-xs text-neutral-500 mt-0.5">{t(`licence.plans.${planKey}Description`, "")}</p>
                        <p className="mt-1 text-xs font-medium text-primary-600">
                          {t("licence.changePlan.maxSecretaries", { count: plan.maxSecretaries })}
                        </p>
                        {hasTooFewSecretaries && (
                          <p className="mt-1 text-xs font-medium text-warning-800">
                            {t("licence.create.planTooFewSecretaries", { count: currentSecretaryCount })}
                          </p>
                        )}
                        {plan.features && plan.features.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {plan.features.slice(0, 4).map((feature) => {
                              const featureKey = `feature${feature.charAt(0) + feature.slice(1).toLowerCase().replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase())}`;
                              return (
                                <li key={feature} className="flex items-start gap-1.5 text-xs text-neutral-600">
                                  <span className="text-success-500 font-bold mt-0.5">✓</span>
                                  <span>{t(`licence.plans.${featureKey}`, feature)}</span>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                        <div className="pt-2 mt-3 border-t border-neutral-100">
                          <p className="text-xs text-neutral-500">{t("licence.changePlan.basePrice")}</p>
                          <p className="text-sm font-bold text-neutral-900">{fmt(plan.basePrice)}</p>
                          <p className="text-xs text-neutral-400">+ {fmt(plan.pricePerDoctor)} / {t("licence.changePlan.perDoctor")}</p>
                        </div>
                        {isSelected && (
                          <div className="absolute flex items-center justify-center w-5 h-5 rounded-full top-2 left-2 bg-primary-500">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Doctor count */}
              {selectedPlan && (
                <div>
                  <p className="mb-3 text-sm font-medium text-neutral-700">
                    {t("licence.changePlan.doctorCount")}
                  </p>
                  <div className="flex items-center gap-4">
                    <button
                      aria-label="decrease"
                      onClick={() => setDoctorCount((n) => Math.max(minDoctorCount, n - 1))}
                      disabled={doctorCount <= minDoctorCount}
                      className="flex items-center justify-center w-10 h-10 transition-colors border-2 rounded-full border-neutral-200 text-neutral-600 hover:border-primary-400 hover:text-primary-600 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-10 text-2xl font-bold text-center text-neutral-900">{doctorCount}</span>
                    <button
                      aria-label="increase"
                      onClick={() => setDoctorCount((n) => n + 1)}
                      className="flex items-center justify-center w-10 h-10 transition-colors border-2 rounded-full border-neutral-200 text-neutral-600 hover:border-primary-400 hover:text-primary-600"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-neutral-500">{t("licence.changePlan.doctors")}</span>
                  </div>
                  {currentDoctorCount > 1 && (
                    <p className="mt-2 text-xs text-neutral-500">
                      {t("licence.create.doctorFloor", { count: currentDoctorCount })}
                    </p>
                  )}
                </div>
              )}

              {/* Duration selector */}
              {selectedPlan && (
                <div>
                  <p className="mb-3 text-sm font-medium text-neutral-700">
                    {t("licence.renew.selectDuration")}
                  </p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {durationOptions.map(({ value, discount }) => (
                      <button
                        key={value}
                        onClick={() => setSelectedDuration(value)}
                        className={`relative px-3 py-3 rounded-xl border-2 text-sm transition-all ${selectedDuration === value
                            ? "border-primary-500 bg-primary-50 text-primary-700"
                            : "border-neutral-200 text-neutral-600 hover:border-primary-300"
                          }`}
                      >
                        {discount > 0 && (
                          <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs bg-success-500 text-white px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                            -{discount}%
                          </span>
                        )}
                        <div className="mt-1 font-semibold">{contractDurationLabel[value]}</div>
                        {selectedDuration === value && (
                          <Check className="w-3.5 h-3.5 mx-auto mt-1 text-primary-600" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Yearly billing-mode selector (MED-1200) — only meaningful for ONE_YEAR */}
              {selectedPlan && selectedDuration === "ONE_YEAR" && (
                <div>
                  <p className="mb-3 text-sm font-medium text-neutral-700">
                    {t("licence.billingMode.title")}
                  </p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <button
                      onClick={() => setSelectedBillingMode("ANNUAL_LUMP_SUM")}
                      className={`relative px-3 py-3 rounded-xl border-2 text-sm text-left transition-all ${selectedBillingMode === "ANNUAL_LUMP_SUM"
                          ? "border-primary-500 bg-primary-50 text-primary-700"
                          : "border-neutral-200 text-neutral-600 hover:border-primary-300"
                        }`}
                    >
                      <span className="absolute -top-2 left-3 text-xs bg-success-500 text-white px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                        -{discountForDuration("ONE_YEAR", "ANNUAL_LUMP_SUM")}%
                      </span>
                      <div className="mt-1 font-semibold">{t("licence.billingMode.fullUpfront")}</div>
                      {selectedBillingMode === "ANNUAL_LUMP_SUM" && (
                        <Check className="w-3.5 h-3.5 mt-1 text-primary-600" />
                      )}
                    </button>
                    <button
                      onClick={() => setSelectedBillingMode("MONTHLY")}
                      className={`relative px-3 py-3 rounded-xl border-2 text-sm text-left transition-all ${selectedBillingMode === "MONTHLY"
                          ? "border-primary-500 bg-primary-50 text-primary-700"
                          : "border-neutral-200 text-neutral-600 hover:border-primary-300"
                        }`}
                    >
                      <span className="absolute -top-2 left-3 text-xs bg-success-500 text-white px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                        -{discountForDuration("ONE_YEAR", "MONTHLY")}%
                      </span>
                      <div className="mt-1 font-semibold">{t("licence.billingMode.monthlyCommitment")}</div>
                      {selectedBillingMode === "MONTHLY" && (
                        <Check className="w-3.5 h-3.5 mt-1 text-primary-600" />
                      )}
                    </button>
                  </div>
                  <div className="flex items-start gap-2 p-3 mt-2 text-sm border rounded-lg bg-warning-50 border-warning-200 text-warning-800">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{t("licence.billingMode.commitmentNote")}</span>
                  </div>
                </div>
              )}

              {/* Price summary */}
              {selectedPlan && (
                <div className="p-4 space-y-2 bg-neutral-50 rounded-xl">
                  <p className="text-sm font-medium text-neutral-700">{t("licence.changePlan.priceSummary")}</p>
                  {(() => {
                    const base = selectedPlan.basePrice + doctorCount * selectedPlan.pricePerDoctor;
                    const isOneYear = selectedDuration === "ONE_YEAR";
                    const isLumpSum = isOneYear && selectedBillingMode === "ANNUAL_LUMP_SUM";
                    const durDiscount = discountForDuration(selectedDuration, selectedBillingMode);
                    const { discount: savings, amount: monthly } = applyDurationDiscount(base, durDiscount);
                    return (
                      <>
                        <div className="flex justify-between text-sm text-neutral-600">
                          <span>{t("licence.changePlan.licenceTypePriceLabel")}</span>
                          <span>{fmt(selectedPlan.basePrice)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-neutral-600">
                          <span>{t("licence.changePlan.doctorPriceLabel")} ({fmt(selectedPlan.pricePerDoctor)} × {doctorCount})</span>
                          <span>{fmt(doctorCount * selectedPlan.pricePerDoctor)}</span>
                        </div>
                        {durDiscount > 0 && (
                          <div className="flex justify-between text-sm text-success-600">
                            <span>{t("licence.changePlan.durationDiscount")} ({durDiscount}%)</span>
                            <span>-{fmt(savings)}</span>
                          </div>
                        )}
                        {isLumpSum ? (
                          <>
                            <div className="flex justify-between text-sm text-neutral-600">
                              <span>{t("licence.billingMode.monthlyRateAfterDiscount")}</span>
                              <span>{fmt(monthly)}</span>
                            </div>
                            <div className="flex justify-between text-sm text-neutral-600">
                              <span>{t("licence.billingMode.duration")}</span>
                              <span>× 12 {t("licence.billingMode.months")}</span>
                            </div>
                            <div className="flex justify-between pt-2 font-semibold border-t text-neutral-900 border-neutral-200">
                              <span>{t("licence.billingMode.totalDueToday")}</span>
                              <span className="text-lg text-primary-600">{fmt(yearTotal(monthly, today))}</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <MonthlyTotalRows
                              label={t("licence.changePlan.monthlyTotal")}
                              monthly={monthly}
                              promotion={activePromotion}
                              fmt={fmt}
                              formatDate={formatDate}
                              t={t}
                            />
                            {isOneYear && (
                              <div className="flex justify-between text-xs text-neutral-500">
                                <span>{t("licence.billingMode.totalOverYear")}</span>
                                <span>{fmt(yearTotal(monthly, today))}</span>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              <ActionError error={actionError} />
              <div className="flex items-center justify-end gap-3 pt-1">
                <button
                  onClick={handleCreateLicence}
                  disabled={!selectedPlan || submitting || !!openPayment}
                  className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white transition-colors rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting && (
                    <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                  )}
                  {t("licence.onboarding.chooseLicence")}
                </button>
              </div>
            </div>
          )}
        </Panel>
      )}

      {successMsg && (
        <div className="flex items-center gap-3 p-4 border bg-success-50 border-success-200 rounded-xl text-success-700">
          <Check className="flex-shrink-0 w-5 h-5" />
          <span className="text-sm">{successMsg}</span>
        </div>
      )}

      {!loading && !error && licence && (
        <>
          {/* Discount lost warning */}
          {licence.discountLost && (
            <div className="flex items-start gap-3 p-4 border bg-warning-50 border-warning-200 rounded-xl text-warning-800">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold">{t("licence.discountLost.title")}</p>
                <p className="text-xs mt-0.5 text-warning-700">{t("licence.discountLost.description")}</p>
              </div>
            </div>
          )}

          {/* Free trial countdown (MED-1220) — no payment-due or invoice UI applies during TRIAL */}
          {licence.status === "TRIAL" && (
            <div className="flex items-start gap-3 p-4 border bg-blue-50 border-blue-200 rounded-xl text-blue-800">
              <Clock className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-600" />
              <div>
                <p className="text-sm font-semibold">
                  {remainingMonths(licence.endDate) >= 1
                    ? t("licence.trial.monthsRemaining", { count: remainingMonths(licence.endDate) })
                    : t("licence.trial.daysRemaining", { count: remainingDays(licence.endDate) })}
                </p>
                <p className="text-xs mt-0.5 text-blue-700">{t("licence.trial.description")}</p>
              </div>
            </div>
          )}

          {/* Locked into monthly billing-mode commitment (MED-1200) */}
          {licence.contractDuration === "ONE_YEAR" &&
            licence.paymentBillingMode === "MONTHLY" &&
            parseDateOnly(licence.endDate) > new Date() && (
              <div className="flex items-start gap-3 p-4 border bg-warning-50 border-warning-200 rounded-xl text-warning-800">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold">
                    {t("licence.billingMode.lockedNote", { endDate: formatDate(licence.endDate) })}
                  </p>
                </div>
              </div>
            )}

          {/* Current licence card */}
          <div className="overflow-hidden bg-white border shadow-sm border-neutral-200 rounded-2xl">
            <div className="px-6 py-5 bg-ews-primary">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-white/20">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-medium tracking-wide uppercase text-white/80">
                      {t("licence.currentPlan")}
                    </p>
                    <h2 className="text-xl font-bold text-white">{licence.licenceType}</h2>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${currentStatusBadge.color}`}>
                    {currentStatusBadge.label}
                  </span>
                  {/* Add doctors immediately */}
                  <ActionButton
                    icon={<UserPlus className="w-4 h-4" />}
                    label={t("licence.addDoctorNow.button")}
                    active={activePanel === "addDoctorNow"}
                    onClick={() => { setAddDoctorNowCount(1); openPanel("addDoctorNow"); }}
                  />
                  {/* Change plan (upgrade or downgrade) */}
                  <ActionButton
                    icon={<TrendingUp className="w-4 h-4" />}
                    label={t("licence.changePlan.button")}
                    active={activePanel === "changePlan"}
                    onClick={() => openPanel("changePlan")}
                  />
                  {/* Renew — only shown when close to expiry. Not applicable during a
                      free trial: there is no real contract yet to renew. */}
                  {licence.status !== "TRIAL" && canRenew(licence.contractDuration, licence.endDate) && (
                    <ActionButton
                      icon={<RefreshCw className="w-4 h-4" />}
                      label={t("licence.renew.button")}
                      active={activePanel === "renew"}
                      onClick={() => openPanel("renew")}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 p-6 sm:grid-cols-3">
              {/* MED-1220: during a free trial, "Monthly Amount" and "Contract
                  Duration" describe the eventual paid contract, not anything
                  currently owed or committed to — showing them under their
                  normal labels reads as if billing/commitment already
                  started. Relabel (and, for contract duration, swap the
                  value) to reflect the trial instead. */}
              <InfoCell
                icon={<BadgeCheck className="w-4 h-4 text-primary-600" />}
                label={
                  licence.status === "TRIAL"
                    ? t("licence.fields.priceAfterTrial")
                    : t("licence.fields.monthlyAmount")
                }
                value={fmt(licence.monthlyAmount)}
                sub={
                  licence.status === "TRIAL"
                    ? t("licence.fields.paymentStartsOn", { date: formatDate(licence.endDate) })
                    : licence.discountLost
                      ? t("licence.fields.noDiscount")
                      : undefined
                }
              />
              <InfoCell
                icon={<Clock className="w-4 h-4 text-primary-600" />}
                label={
                  licence.status === "TRIAL"
                    ? t("licence.fields.trialDuration")
                    : t("licence.fields.contractDuration")
                }
                value={
                  licence.status === "TRIAL"
                    ? t("licence.trial.durationValue", {
                        count: licence.trialDurationMonths ?? 0,
                      })
                    : (contractDurationLabel[licence.contractDuration] ?? licence.contractDuration)
                }
              />
              <InfoCell
                icon={<Users className="w-4 h-4 text-primary-600" />}
                label={t("licence.fields.numberOfDoctors")}
                value={String(licence.numberOfDoctors)}
              />
              <InfoCell
                icon={<Users className="w-4 h-4 text-neutral-400" />}
                label={t("licence.fields.maxSecretaries")}
                value={String(licence.maxSecretaries)}
              />
              <InfoCell
                icon={<Calendar className="w-4 h-4 text-success-600" />}
                label={t("licence.fields.startDate")}
                value={formatDate(licence.startDate)}
              />
              <InfoCell
                icon={<Calendar className="w-4 h-4 text-danger-500" />}
                label={
                  licence.status === "TRIAL"
                    ? t("licence.fields.trialEndDate")
                    : t("licence.fields.endDate")
                }
                value={formatDate(licence.endDate)}
              />
            </div>

            {/* Payment info bar — superseded by the Pending Invoice card below once
                anything is actually owed, so it only shows the proactive "you're
                current, here's your next due date" case. */}
            {licence.paymentDueDate && !pendingInvoice && (
              <div className="flex items-center gap-2 px-6 py-3 text-sm border-t border-neutral-100 bg-neutral-50 text-neutral-600">
                <Clock className="w-4 h-4 text-neutral-400" />
                <span>
                  {t("licence.fields.nextPayment")}:{" "}
                  <span className="font-semibold text-neutral-900">
                    {formatDate(licence.paymentDueDate)}
                  </span>
                  {" — "}
                  <span className={licence.discountLost ? "font-semibold text-warning-700" : "font-semibold"}>
                    {fmt(licence.monthlyAmount)}
                  </span>
                </span>
              </div>
            )}

            {/* Pending pro-rated charges bar */}
            {licence.pendingProRatedCharges > 0 && (
              <div className="flex items-center gap-2 px-6 py-3 text-sm border-t border-neutral-100 bg-amber-50 text-amber-700">
                <AlertTriangle className="flex-shrink-0 w-4 h-4 text-amber-500" />
                <span>
                  {t("licence.fields.pendingCharges")}: <span className="font-semibold">{fmt(licence.pendingProRatedCharges)}</span>
                  {" "}{t("licence.fields.pendingChargesNote")}
                </span>
              </div>
            )}

            {/* Pending plan/doctor-count changes bar */}
            {(licence.pendingLicenceType || licence.pendingNumberOfDoctors) && (
              <div className="flex items-center gap-2 px-6 py-3 text-sm text-blue-700 border-t border-neutral-100 bg-blue-50">
                <Clock className="flex-shrink-0 w-4 h-4 text-blue-500" />
                <span>
                  {t("licence.pending.title")}:{" "}
                  {licence.pendingLicenceType && licence.pendingLicenceType !== licence.licenceType && (
                    <span className="font-semibold">{t("licence.pending.planChange", { plan: licence.pendingLicenceType })}</span>
                  )}
                  {licence.pendingNumberOfDoctors && licence.pendingNumberOfDoctors !== licence.numberOfDoctors && (
                    <span className="ml-1 font-semibold">{t("licence.pending.doctorChange", { count: licence.pendingNumberOfDoctors })}</span>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* ── ACTIVE PROMOTION (MED-1172) ── */}
          {activePromotion && (
            <PromotionSection
              promotion={activePromotion}
              monthlyAmount={licence.monthlyAmount}
              discountedMonthlyAmount={activePromotion.discountedMonthlyAmount ?? licence.discountedMonthlyAmount}
              fmt={fmt}
              formatDate={formatDate}
              t={t}
            />
          )}

          {/* ── PENDING INVOICE PAYMENT ── */}
          {pendingInvoice && !panelReplacesInvoice && (
            <div className="overflow-hidden bg-white border shadow-sm border-primary-200 rounded-2xl">
              <div className="flex items-center gap-2 px-6 py-4 border-b border-neutral-100">
                <BadgeCheck className="w-5 h-5 text-primary-600" />
                <div>
                  <h3 className="font-semibold text-neutral-900">{t("payment.invoice.title")}</h3>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    {t("payment.invoice.period")}:{" "}
                    {formatBillingPeriod(pendingInvoice.billingPeriod, i18n.language, pendingInvoice.periodMonths)}
                    {pendingInvoice.dueDate && (
                      <> — {t("payment.invoice.dueDate")}: {formatDate(pendingInvoice.dueDate)}</>
                    )}
                  </p>
                </div>
                {/* MED-1584: when a rebate promotion discounted this invoice, the API sends the
                    amount before the promotion and the promotion applied — show the breakdown. */}
                {pendingInvoice.baseAmount != null && pendingInvoice.rebatePercentage != null ? (
                  <div className="ml-auto space-y-0.5 text-right" data-testid="pending-invoice-discount">
                    <p className="text-xs text-neutral-500">
                      {t("payment.invoice.baseAmount")}: <span className="line-through">{fmt(pendingInvoice.baseAmount)}</span>
                    </p>
                    <p className="text-xs text-success-600">
                      {t("payment.invoice.promotionLine", {
                        code: pendingInvoice.promotionCode ?? "",
                        rebate: pendingInvoice.rebatePercentage,
                      })}
                      : -{fmt(pendingInvoice.baseAmount - pendingInvoice.amount)}
                    </p>
                    <p className="text-xs text-neutral-500">{t("payment.invoice.amountToPay")}</p>
                    <p className="text-lg font-bold text-primary-600">{fmt(pendingInvoice.amount)}</p>
                  </div>
                ) : (
                  <div className="ml-auto text-right">
                    <p className="text-xs text-neutral-500">{t("payment.invoice.amount")}</p>
                    <p className="text-lg font-bold text-primary-600">{fmt(pendingInvoice.amount)}</p>
                  </div>
                )}
              </div>
              <div className="px-6 py-4">
                {/* Yearly billing-mode choice (MED-1200) — stays editable until the invoice
                    is actually paid (e.g. a manager who backed out of PayDunya can still
                    switch between full-upfront and monthly-commitment). */}
                {canChooseBillingMode && (
                  <div className="mb-4">
                    <p className="mb-3 text-sm font-medium text-neutral-700">
                      {t("licence.billingMode.title")}
                    </p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <button
                        onClick={() => setPendingInvoiceBillingMode("ANNUAL_LUMP_SUM")}
                        className={`relative px-3 py-3 rounded-xl border-2 text-sm text-left transition-all ${pendingInvoiceBillingMode === "ANNUAL_LUMP_SUM"
                            ? "border-primary-500 bg-primary-50 text-primary-700"
                            : "border-neutral-200 text-neutral-600 hover:border-primary-300"
                          }`}
                      >
                        <span className="absolute -top-2 left-3 text-xs bg-success-500 text-white px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                          -{discountForDuration("ONE_YEAR", "ANNUAL_LUMP_SUM")}%
                        </span>
                        <div className="mt-1 font-semibold">{t("licence.billingMode.fullUpfront")}</div>
                        {pendingInvoiceBillingMode === "ANNUAL_LUMP_SUM" && (
                          <Check className="w-3.5 h-3.5 mt-1 text-primary-600" />
                        )}
                      </button>
                      <button
                        onClick={() => setPendingInvoiceBillingMode("MONTHLY")}
                        className={`relative px-3 py-3 rounded-xl border-2 text-sm text-left transition-all ${pendingInvoiceBillingMode === "MONTHLY"
                            ? "border-primary-500 bg-primary-50 text-primary-700"
                            : "border-neutral-200 text-neutral-600 hover:border-primary-300"
                          }`}
                      >
                        <span className="absolute -top-2 left-3 text-xs bg-success-500 text-white px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                          -{discountForDuration("ONE_YEAR", "MONTHLY")}%
                        </span>
                        <div className="mt-1 font-semibold">{t("licence.billingMode.monthlyCommitment")}</div>
                        {pendingInvoiceBillingMode === "MONTHLY" && (
                          <Check className="w-3.5 h-3.5 mt-1 text-primary-600" />
                        )}
                      </button>
                    </div>
                    <div className="flex items-start gap-2 p-3 mt-2 text-sm border rounded-lg bg-warning-50 border-warning-200 text-warning-800">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{t("licence.billingMode.commitmentNote")}</span>
                    </div>

                    {/* Live price breakdown for the selected billing mode */}
                    <div className="p-4 mt-3 space-y-2 bg-neutral-50 rounded-xl">
                      <p className="text-sm font-medium text-neutral-700">{t("licence.renew.priceSummary")}</p>
                      {(() => {
                        const base = licence.baseAmount;
                        const currentPlanData = plans.find((p) => p.type === licence.licenceType);
                        const isLumpSum = pendingInvoiceBillingMode === "ANNUAL_LUMP_SUM";
                        const durDiscount = discountForDuration("ONE_YEAR", pendingInvoiceBillingMode);
                        const effectiveDiscount = licence.discountLost ? 0 : durDiscount;
                        const { discount: savings, amount: monthly } = applyDurationDiscount(base, effectiveDiscount);
                        return (
                          <>
                            <div className="flex justify-between text-sm text-neutral-600">
                              <span>{t("licence.renew.licenceTypePriceLabel")} ({licence.licenceType})</span>
                              <span>{fmt(currentPlanData?.basePrice ?? 0)}</span>
                            </div>
                            <div className="flex justify-between text-sm text-neutral-600">
                              <span>{t("licence.renew.doctorPriceLabel")} ({fmt(currentPlanData?.pricePerDoctor ?? 0)} × {licence.numberOfDoctors})</span>
                              <span>{fmt((currentPlanData?.pricePerDoctor ?? 0) * licence.numberOfDoctors)}</span>
                            </div>
                            {effectiveDiscount > 0 && (
                              <div className="flex justify-between text-sm text-success-600">
                                <span>{t("licence.renew.discount")} ({effectiveDiscount}%)</span>
                                <span>-{fmt(savings)}</span>
                              </div>
                            )}
                            {isLumpSum ? (
                              <>
                                <div className="flex justify-between text-sm text-neutral-600">
                                  <span>{t("licence.billingMode.monthlyRateAfterDiscount")}</span>
                                  <span>{fmt(monthly)}</span>
                                </div>
                                <div className="flex justify-between text-sm text-neutral-600">
                                  <span>{t("licence.billingMode.duration")}</span>
                                  <span>× 12 {t("licence.billingMode.months")}</span>
                                </div>
                                <div className="flex justify-between pt-2 font-semibold border-t text-neutral-900 border-neutral-200">
                                  <span>{t("licence.billingMode.totalDueToday")}</span>
                                  <span className="text-lg text-primary-600">{fmt(yearTotal(monthly, today))}</span>
                                </div>
                              </>
                            ) : (
                              <>
                                <MonthlyTotalRows
                                  label={t("licence.billingMode.monthlyAmount")}
                                  monthly={monthly}
                                  promotion={activePromotion}
                                  fmt={fmt}
                                  formatDate={formatDate}
                                  t={t}
                                />
                                <div className="flex justify-between text-xs text-neutral-500">
                                  <span>{t("licence.billingMode.totalOverYear")}</span>
                                  <span>{fmt(yearTotal(monthly, today))}</span>
                                </div>
                              </>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
                {paymentError && (
                  <div className="flex items-center gap-2 p-3 mb-3 text-sm border rounded-lg bg-danger-50 border-danger-200 text-danger-700">
                    <AlertCircle className="flex-shrink-0 w-4 h-4" />
                    {paymentError}
                  </div>
                )}
                <div className="flex justify-end">
                  <button
                    onClick={() => void handlePayInvoice()}
                    disabled={paymentInitiating || !!openPayment}
                    className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {paymentInitiating && (
                      <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                    )}
                    {paymentInitiating ? t("payment.initiate.loading") : t("payment.initiate.button")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {!panelReplacesInvoice && <PaymentSupportMessage />}

          {/* ── ADD DOCTORS NOW PANEL ── */}
          {activePanel === "addDoctorNow" && (
            <div ref={panelSectionRef} className="scroll-mt-24">
            <Panel
              icon={<UserPlus className="w-5 h-5 text-primary-600" />}
              title={t("licence.addDoctorNow.title")}
              subtitle={t("licence.addDoctorNow.subtitle")}
            >
              {plansLoading ? (
                <Spinner />
              ) : (
                <div className="space-y-5">
                  <div>
                    <p className="mb-3 text-sm font-medium text-neutral-700">
                      {t("licence.addDoctorNow.doctorCount")}
                    </p>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setAddDoctorNowCount((n) => Math.max(1, n - 1))}
                        className="flex items-center justify-center w-10 h-10 transition-colors border-2 rounded-full border-neutral-200 text-neutral-600 hover:border-primary-400 hover:text-primary-600"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-10 text-2xl font-bold text-center text-neutral-900">
                        {addDoctorNowCount}
                      </span>
                      <button
                        onClick={() => setAddDoctorNowCount((n) => n + 1)}
                        className="flex items-center justify-center w-10 h-10 transition-colors border-2 rounded-full border-neutral-200 text-neutral-600 hover:border-primary-400 hover:text-primary-600"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-neutral-500">{t("licence.changePlan.doctors")}</span>
                    </div>
                  </div>

                  {/* Pro-rated cost breakdown */}
                  {(() => {
                    const currentPlanData = plans.find((p) => p.type === licence.licenceType);
                    const pricePerDoctor = currentPlanData?.pricePerDoctor ?? 0;
                    const now = new Date();
                    const due = licence.paymentDueDate ? parseDateOnly(licence.paymentDueDate) : null;
                    const daysLeft = due ? Math.max(0, Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))) : 30;
                    const perDoctor = due
                      ? Math.min(Math.ceil((pricePerDoctor / 30) * daysLeft * 1.03), pricePerDoctor)
                      : pricePerDoctor;
                    const total = perDoctor * addDoctorNowCount;
                    return (
                      <div className="p-4 space-y-2 border bg-primary-50 rounded-xl border-primary-100">
                        <p className="text-sm font-medium text-neutral-700">{t("licence.addDoctorNow.priceSummary")}</p>
                        <div className="flex justify-between text-sm text-neutral-600">
                          <span>{t("licence.addDoctorNow.daysLeft")}</span>
                          <span className="font-medium">{daysLeft} {t("licence.addDoctorNow.days")}</span>
                        </div>
                        <div className="flex justify-between text-sm text-neutral-600">
                          <span>{t("licence.addDoctorNow.perDoctorCost")}</span>
                          <span className="font-medium">{fmt(perDoctor)}</span>
                        </div>
                        <div className="flex justify-between text-sm italic text-neutral-400">
                          <span>{t("licence.addDoctorNow.formula")}</span>
                        </div>
                        <div className="flex justify-between pt-2 font-semibold border-t text-neutral-900 border-primary-200">
                          <span>{t("licence.addDoctorNow.totalCost")}</span>
                          <span className="text-lg text-primary-600">{fmt(total)}</span>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="flex items-start gap-2 p-3 text-sm text-blue-800 border border-blue-200 rounded-lg bg-blue-50">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{t("licence.addDoctorNow.note")}</span>
                  </div>

                  <ActionError error={actionError} />
                  <PanelActions
                    onCancel={closePanel}
                    onConfirm={handleAddDoctorsNow}
                    confirmLabel={t("licence.addDoctorNow.confirm")}
                    cancelLabel={t("licence.addDoctorNow.cancel")}
                    submitting={submitting}
                  />
                </div>
              )}
            </Panel>
            </div>
          )}

          {/* ── CHANGE PLAN PANEL ── */}
          {activePanel === "changePlan" && (
            <div ref={panelSectionRef} className="space-y-6 scroll-mt-24">
            <button
              type="button"
              onClick={closePanel}
              className="flex items-center gap-2 text-sm font-medium transition-colors text-primary-600 hover:text-primary-700"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("common.back")}
            </button>
            <Panel
              icon={isDowngrade ? <TrendingDown className="w-5 h-5 text-primary-600" /> : <TrendingUp className="w-5 h-5 text-primary-600" />}
              title={t("licence.changePlan.title")}
              subtitle={t("licence.changePlan.subtitle")}
            >
              {plansLoading ? (
                <Spinner />
              ) : (
                <div className="space-y-6">
                  {/* Plan selector */}
                  <div>
                    <p className="mb-3 text-sm font-medium text-neutral-700">
                      {t("licence.changePlan.selectPlan")}
                    </p>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      {plans.map((plan) => {
                        const planKey = plan.type.toLowerCase().replace("professionnel", "professional");
                        const isSelected = selectedPlan?.type === plan.type;
                        const isCurrent = plan.type === licence.licenceType;
                        return (
                          <div
                            key={plan.type}
                            className={`relative overflow-hidden border rounded-xl p-5 cursor-pointer transition-all ${isSelected ? "border-primary-500 bg-primary-50 shadow-md" : "border-neutral-200 hover:border-primary-300"
                              }`}
                            onClick={() => setSelectedPlan(plan)}
                          >
                            {isCurrent && (
                              <span className="absolute top-2 right-2 text-xs bg-neutral-200 text-neutral-600 px-2 py-0.5 rounded-full font-medium">
                                {t("licence.changePlan.current")}
                              </span>
                            )}
                            <h3 className="mt-1 font-semibold text-neutral-900">{t(`licence.plans.${planKey}`, plan.type)}</h3>
                            <p className="text-xs text-neutral-500 mt-0.5">{t(`licence.plans.${planKey}Description`, "")}</p>
                            <p className="mt-1 text-xs font-medium text-primary-600">
                              {t("licence.changePlan.maxSecretaries", { count: plan.maxSecretaries })}
                            </p>
                            {plan.features && plan.features.length > 0 && (
                              <ul className="mt-2 space-y-1">
                                {plan.features.slice(0, 4).map((feature) => {
                                  const featureKey = `feature${feature.charAt(0) + feature.slice(1).toLowerCase().replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase())}`;
                                  return (
                                    <li key={feature} className="flex items-start gap-1.5 text-xs text-neutral-600">
                                      <span className="text-success-500 font-bold mt-0.5">✓</span>
                                      <span>{t(`licence.plans.${featureKey}`, feature)}</span>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                            <div className="pt-2 mt-3 border-t border-neutral-100">
                              <p className="text-xs text-neutral-500">{t("licence.changePlan.basePrice")}</p>
                              <p className="text-sm font-bold text-neutral-900">{fmt(plan.basePrice)}</p>
                              <p className="text-xs text-neutral-400">+ {fmt(plan.pricePerDoctor)} / {t("licence.changePlan.perDoctor")}</p>
                            </div>
                            {isSelected && (
                              <div className="absolute flex items-center justify-center w-5 h-5 rounded-full top-2 left-2 bg-primary-500">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Doctor count */}
                  {selectedPlan && (
                    <div>
                      <p className="mb-3 text-sm font-medium text-neutral-700">
                        {t("licence.changePlan.doctorCount")}
                      </p>
                      <div className="flex items-center gap-4">
                        <button
                          aria-label="decrease"
                          onClick={() => setDoctorCount((n) => Math.max(1, n - 1))}
                          className="flex items-center justify-center w-10 h-10 transition-colors border-2 rounded-full border-neutral-200 text-neutral-600 hover:border-primary-400 hover:text-primary-600"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-10 text-2xl font-bold text-center text-neutral-900">{doctorCount}</span>
                        <button
                          aria-label="increase"
                          onClick={() => setDoctorCount((n) => n + 1)}
                          className="flex items-center justify-center w-10 h-10 transition-colors border-2 rounded-full border-neutral-200 text-neutral-600 hover:border-primary-400 hover:text-primary-600"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <span className="text-sm text-neutral-500">{t("licence.changePlan.doctors")}</span>
                      </div>
                    </div>
                  )}

                  {/* Price summary */}
                  {selectedPlan && (
                    <div className="p-4 space-y-2 bg-neutral-50 rounded-xl">
                      <p className="text-sm font-medium text-neutral-700">{t("licence.changePlan.priceSummary")}</p>
                      {(() => {
                        const base = selectedPlan.basePrice + doctorCount * selectedPlan.pricePerDoctor;
                        const durDiscount = discountForDuration(licence.contractDuration as ContractDuration, licence.paymentBillingMode);
                        const effectiveDiscount = licence.discountLost ? 0 : durDiscount;
                        const { discount: savings, amount: monthly } = applyDurationDiscount(base, effectiveDiscount);
                        return (
                          <>
                            <div className="flex justify-between text-sm text-neutral-600">
                              <span>{t("licence.changePlan.licenceTypePriceLabel")}</span>
                              <span>{fmt(selectedPlan.basePrice)}</span>
                            </div>
                            <div className="flex justify-between text-sm text-neutral-600">
                              <span>{t("licence.changePlan.doctorPriceLabel")} ({fmt(selectedPlan.pricePerDoctor)} × {doctorCount})</span>
                              <span>{fmt(doctorCount * selectedPlan.pricePerDoctor)}</span>
                            </div>
                            {effectiveDiscount > 0 && (
                              <div className="flex justify-between text-sm text-success-600">
                                <span>{t("licence.changePlan.durationDiscount")} ({effectiveDiscount}%)</span>
                                <span>-{fmt(savings)}</span>
                              </div>
                            )}
                            {licence.discountLost && durDiscount > 0 && (
                              <div className="flex justify-between text-sm text-warning-600">
                                <span>{t("licence.discountLost.noDiscountApplied")}</span>
                                <span>-{fmt(0)}</span>
                              </div>
                            )}
                            <MonthlyTotalRows
                              label={t("licence.changePlan.monthlyTotal")}
                              monthly={monthly}
                              promotion={activePromotion}
                              fmt={fmt}
                              formatDate={formatDate}
                              t={t}
                            />
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {isDowngrade && (
                    <div className="flex items-start gap-2 p-3 text-sm border rounded-lg bg-warning-50 border-warning-200 text-warning-800">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{t("licence.changePlan.downgradeWarning")}</span>
                    </div>
                  )}

                  {/* ── Doctor removal section — all doctors shown, scheduled ones pre-checked ── */}
                  {showDoctorRemoval && (
                    <RemovalSection
                      title={t("licence.changePlan.selectDoctorsToRemove")}
                      subtitle={
                        neededDoctorRemovals > 0
                          ? t("licence.changePlan.selectDoctorsToRemoveSubtitle", { count: neededDoctorRemovals })
                          : t("licence.changePlan.scheduledRemovalsReview")
                      }
                      note={t("licence.changePlan.removalNote")}
                      selectedCount={selectedDoctorsToRemove.length}
                      neededCount={neededDoctorRemovals}
                      selectedCountLabel={t("licence.changePlan.selectedCount", { selected: selectedDoctorsToRemove.length, needed: neededDoctorRemovals })}
                      loading={doctorsLoading}
                      people={facilityDoctors}
                      selectedIds={selectedDoctorsToRemove}
                      scheduledIds={scheduledDoctorIds}
                      onToggle={(id) =>
                        setSelectedDoctorsToRemove((prev) =>
                          prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                        )
                      }
                    />
                  )}

                  {/* ── Secretary removal section — all secretaries shown, scheduled ones pre-checked ── */}
                  {showSecretaryRemoval && (
                    <RemovalSection
                      title={t("licence.changePlan.selectSecretariesToRemove")}
                      subtitle={
                        neededSecretaryRemovals > 0
                          ? t("licence.changePlan.selectSecretariesToRemoveSubtitle", { count: neededSecretaryRemovals })
                          : t("licence.changePlan.scheduledRemovalsReview")
                      }
                      note={t("licence.changePlan.removalNote")}
                      selectedCount={selectedSecretariesToRemove.length}
                      neededCount={neededSecretaryRemovals}
                      selectedCountLabel={t("licence.changePlan.selectedCount", { selected: selectedSecretariesToRemove.length, needed: neededSecretaryRemovals })}
                      loading={secretariesLoading}
                      people={facilitySecretaries}
                      selectedIds={selectedSecretariesToRemove}
                      scheduledIds={scheduledSecretaryIds}
                      onToggle={(id) =>
                        setSelectedSecretariesToRemove((prev) =>
                          prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                        )
                      }
                    />
                  )}

                  <ActionError error={actionError} />
                  <PanelActions
                    onCancel={closePanel}
                    onConfirm={handleChangePlan}
                    confirmLabel={
                      neededDoctorRemovals > 0 || neededSecretaryRemovals > 0
                        ? t("licence.changePlan.confirmRemoval")
                        : isDowngrade
                          ? t("licence.changePlan.confirmDowngrade")
                          : isUpgrade
                            ? t("licence.changePlan.confirmUpgrade")
                            : t("licence.changePlan.confirm")
                    }
                    cancelLabel={t("licence.changePlan.cancel")}
                    submitting={submitting}
                    disabled={
                      !selectedPlan ||
                      selectedDoctorsToRemove.length < neededDoctorRemovals ||
                      selectedSecretariesToRemove.length < neededSecretaryRemovals
                    }
                  />
                </div>
              )}
            </Panel>
            </div>
          )}

          {/* ── RENEW PANEL ── */}
          {activePanel === "renew" && (
            <Panel
              icon={<RefreshCw className="w-5 h-5 text-primary-600" />}
              title={t("licence.renew.title")}
              subtitle={t("licence.renew.subtitle")}
            >
              <div className="space-y-5">
                <div>
                  <p className="mb-3 text-sm font-medium text-neutral-700">
                    {t("licence.renew.selectDuration")}
                  </p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {durationOptions.map(({ value, discount }) => (
                      <button
                        key={value}
                        onClick={() => setSelectedDuration(value)}
                        className={`relative px-3 py-3 rounded-xl border-2 text-sm transition-all ${selectedDuration === value
                            ? "border-primary-500 bg-primary-50 text-primary-700"
                            : "border-neutral-200 text-neutral-600 hover:border-primary-300"
                          }`}
                      >
                        {discount > 0 && !licence.discountLost && (
                          <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs bg-success-500 text-white px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                            -{discount}%
                          </span>
                        )}
                        <div className="mt-1 font-semibold">{contractDurationLabel[value]}</div>
                        {selectedDuration === value && (
                          <Check className="w-3.5 h-3.5 mx-auto mt-1 text-primary-600" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Yearly billing-mode selector (MED-1200) — only meaningful for ONE_YEAR */}
                {selectedDuration === "ONE_YEAR" && (
                  <div>
                    <p className="mb-3 text-sm font-medium text-neutral-700">
                      {t("licence.billingMode.title")}
                    </p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <button
                        onClick={() => setSelectedBillingMode("ANNUAL_LUMP_SUM")}
                        className={`relative px-3 py-3 rounded-xl border-2 text-sm text-left transition-all ${selectedBillingMode === "ANNUAL_LUMP_SUM"
                            ? "border-primary-500 bg-primary-50 text-primary-700"
                            : "border-neutral-200 text-neutral-600 hover:border-primary-300"
                          }`}
                      >
                        <span className="absolute -top-2 left-3 text-xs bg-success-500 text-white px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                          -{discountForDuration("ONE_YEAR", "ANNUAL_LUMP_SUM")}%
                        </span>
                        <div className="mt-1 font-semibold">{t("licence.billingMode.fullUpfront")}</div>
                        {selectedBillingMode === "ANNUAL_LUMP_SUM" && (
                          <Check className="w-3.5 h-3.5 mt-1 text-primary-600" />
                        )}
                      </button>
                      <button
                        onClick={() => setSelectedBillingMode("MONTHLY")}
                        className={`relative px-3 py-3 rounded-xl border-2 text-sm text-left transition-all ${selectedBillingMode === "MONTHLY"
                            ? "border-primary-500 bg-primary-50 text-primary-700"
                            : "border-neutral-200 text-neutral-600 hover:border-primary-300"
                          }`}
                      >
                        <span className="absolute -top-2 left-3 text-xs bg-success-500 text-white px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                          -{discountForDuration("ONE_YEAR", "MONTHLY")}%
                        </span>
                        <div className="mt-1 font-semibold">{t("licence.billingMode.monthlyCommitment")}</div>
                        {selectedBillingMode === "MONTHLY" && (
                          <Check className="w-3.5 h-3.5 mt-1 text-primary-600" />
                        )}
                      </button>
                    </div>
                    <div className="flex items-start gap-2 p-3 mt-2 text-sm border rounded-lg bg-warning-50 border-warning-200 text-warning-800">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{t("licence.billingMode.commitmentNote")}</span>
                    </div>
                  </div>
                )}

                {licence.discountLost && (
                  <div className="flex items-start gap-2 p-3 text-sm border rounded-lg bg-warning-50 border-warning-200 text-warning-800">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{t("licence.discountLost.renewNote")}</span>
                  </div>
                )}

                <div className="p-4 space-y-2 bg-neutral-50 rounded-xl">
                  <p className="text-sm font-medium text-neutral-700">{t("licence.renew.priceSummary")}</p>
                  {(() => {
                    const base = licence.baseAmount;
                    const currentPlanData = plans.find((p) => p.type === licence.licenceType);
                    const isOneYear = selectedDuration === "ONE_YEAR";
                    const isLumpSum = isOneYear && selectedBillingMode === "ANNUAL_LUMP_SUM";
                    const durDiscount = discountForDuration(selectedDuration, selectedBillingMode);
                    const effectiveDiscount = licence.discountLost ? 0 : durDiscount;
                    const { discount: savings, amount: total } = applyDurationDiscount(base, effectiveDiscount);
                    return (
                      <>
                        <div className="flex justify-between text-sm text-neutral-600">
                          <span>{t("licence.renew.licenceTypePriceLabel")} ({licence.licenceType})</span>
                          <span>{fmt(currentPlanData?.basePrice ?? 0)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-neutral-600">
                          <span>{t("licence.renew.doctorPriceLabel")} ({fmt(currentPlanData?.pricePerDoctor ?? 0)} × {licence.numberOfDoctors})</span>
                          <span>{fmt((currentPlanData?.pricePerDoctor ?? 0) * licence.numberOfDoctors)}</span>
                        </div>
                        {effectiveDiscount > 0 && (
                          <div className="flex justify-between text-sm text-success-600">
                            <span>{t("licence.renew.discount")} ({effectiveDiscount}%)</span>
                            <span>-{fmt(savings)}</span>
                          </div>
                        )}
                        {isLumpSum ? (
                          <>
                            <div className="flex justify-between text-sm text-neutral-600">
                              <span>{t("licence.billingMode.monthlyRateAfterDiscount")}</span>
                              <span>{fmt(total)}</span>
                            </div>
                            <div className="flex justify-between text-sm text-neutral-600">
                              <span>{t("licence.billingMode.duration")}</span>
                              <span>× 12 {t("licence.billingMode.months")}</span>
                            </div>
                            <div className="flex justify-between pt-2 font-semibold border-t text-neutral-900 border-neutral-200">
                              <span>{t("licence.billingMode.totalDueToday")}</span>
                              <span className="text-lg text-primary-600">{fmt(yearTotal(total, licenceFirstDueDate))}</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <MonthlyTotalRows
                              label={t("licence.renew.monthlyTotal")}
                              monthly={total}
                              promotion={activePromotion}
                              fmt={fmt}
                              formatDate={formatDate}
                              t={t}
                            />
                            {isOneYear && (
                              <div className="flex justify-between text-xs text-neutral-500">
                                <span>{t("licence.billingMode.totalOverYear")}</span>
                                <span>{fmt(yearTotal(total, licenceFirstDueDate))}</span>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>

                <ActionError error={actionError} />
                <PanelActions
                  onCancel={closePanel}
                  onConfirm={handleRenew}
                  confirmLabel={t("licence.renew.confirm")}
                  cancelLabel={t("licence.renew.cancel")}
                  submitting={submitting}
                />
              </div>
            </Panel>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="max-w-5xl p-6 mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">{t("licence.title")}</h1>
        <p className="mt-1 text-sm text-neutral-500">{t("licence.subtitle")}</p>
        {/* Fixed-position overlay; kept out of the space-y stack so it adds no margin. */}
        {openPayment && (
          <PaymentInProgressOverlay
            key={openPayment.intent.paymentIntentId}
            invoiceId={openPayment.invoiceId}
            intent={openPayment.intent}
            onResolved={handleOpenPaymentResolved}
          />
        )}
      </div>

      <TabNavigation
        activeTab={activeTab}
        onTabChange={handleTabChange}
        tabs={[
          {
            id: "licence",
            label: t("licence.tabs.licence"),
            icon: Shield,
            content: licenceTabContent,
          },
          {
            id: "invoices",
            label: t("licence.tabs.invoices"),
            icon: FileText,
            content: (
              <InvoicesTab
                facilityId={facilityId ? Number(facilityId) : undefined}
                facilityName={facilityUser?.healthcareFacility?.name}
                onPay={handlePayListedInvoice}
                payDisabled={!!openPayment}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

/* ── Shared sub-components ── */

function ActionButton({
  icon, label, active, onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${active ? "bg-white text-primary-600" : "bg-white/20 hover:bg-white/30 text-white"
        }`}
    >
      {icon}
      {label}
    </button>
  );
}

function Panel({
  icon, title, subtitle, children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden bg-white border shadow-sm border-primary-200 rounded-2xl">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-neutral-100">
        {icon}
        <div>
          <h3 className="font-semibold text-neutral-900">{title}</h3>
          <p className="text-xs text-neutral-500 mt-0.5">{subtitle}</p>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function ActionError({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div className="flex items-center gap-2 p-3 text-sm border rounded-lg bg-danger-50 border-danger-200 text-danger-700">
      <AlertCircle className="flex-shrink-0 w-4 h-4" />
      {error}
    </div>
  );
}

const PAYMENT_SUPPORT_EMAIL = "support@medecine360.com";

function PaymentSupportMessage() {
  return (
    <div className="my-6 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
      <p className="text-sm text-neutral-600">
        <Trans
          i18nKey="licence.paymentSupport.message"
          values={{ email: PAYMENT_SUPPORT_EMAIL }}
          components={[
            <a
              key="payment-support-email"
              href={`mailto:${PAYMENT_SUPPORT_EMAIL}`}
              className="font-medium text-primary-600 underline"
            />,
          ]}
        />
      </p>
    </div>
  );
}

function PanelActions({
  onCancel, onConfirm, confirmLabel, cancelLabel, submitting, disabled = false,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  cancelLabel: string;
  submitting: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-end gap-3 pt-1">
      <button
        onClick={onCancel}
        className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-900"
      >
        {cancelLabel}
      </button>
      <button
        onClick={onConfirm}
        disabled={disabled || submitting}
        className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white transition-colors rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting && (
          <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
        )}
        {confirmLabel}
      </button>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center py-8">
      <div className="w-6 h-6 border-b-2 rounded-full animate-spin border-primary-600" />
    </div>
  );
}

function RemovalSection({
  title, subtitle, note, selectedCount, neededCount, selectedCountLabel,
  loading, people, selectedIds, scheduledIds, onToggle,
}: {
  title: string;
  subtitle: string;
  note: string;
  selectedCount: number;
  neededCount: number;
  selectedCountLabel: string;
  loading: boolean;
  people: Array<{ id: number; firstName: string; lastName: string; email: string }>;
  selectedIds: number[];
  scheduledIds: number[];   // IDs already scheduled for removal — shown first with a badge
  onToggle: (id: number) => void;
}) {
  // Sort: already-scheduled first, then the rest alphabetically
  const sorted = [...people].sort((a, b) => {
    const aScheduled = scheduledIds.includes(a.id);
    const bScheduled = scheduledIds.includes(b.id);
    if (aScheduled && !bScheduled) return -1;
    if (!aScheduled && bScheduled) return 1;
    return 0;
  });

  return (
    <div className="overflow-hidden border border-warning-200 rounded-xl">
      <div className="flex items-center gap-2 px-4 py-3 border-b bg-warning-50 border-warning-200">
        <Users className="flex-shrink-0 w-4 h-4 text-warning-600" />
        <div>
          <p className="text-sm font-semibold text-warning-800">{title}</p>
          <p className="text-xs text-warning-700 mt-0.5">{subtitle}</p>
        </div>
      </div>
      <div className="p-4 space-y-3">
        {loading ? (
          <Spinner />
        ) : (
          <div className="space-y-2 overflow-y-auto max-h-56">
            {sorted.map((person) => {
              const isScheduled = scheduledIds.includes(person.id);
              const selected = selectedIds.includes(Number(person.id));
              // Can only uncheck if doing so still leaves enough selected to meet the minimum
              const canUncheck = selected && (selectedIds.length - 1 >= neededCount);
              const isDisabled = selected && !canUncheck;
              return (
                <label
                  key={person.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                    } ${selected ? "border-warning-400 bg-warning-50" : "border-neutral-200 hover:border-neutral-300"}`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    disabled={isDisabled}
                    onChange={() => !isDisabled && onToggle(Number(person.id))}
                    className="rounded border-neutral-300 text-warning-600 disabled:cursor-not-allowed"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-neutral-900">{person.firstName} {person.lastName}</p>
                      {isScheduled && (
                        <span className="text-xs bg-warning-100 text-warning-700 border border-warning-300 px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                          scheduled
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-neutral-500">{person.email}</p>
                  </div>
                </label>
              );
            })}
          </div>
        )}
        <p className="text-xs text-warning-700">
          <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
          {note}
        </p>
        <p className={`text-xs font-medium ${selectedCount >= neededCount ? "text-success-600" : "text-neutral-500"}`}>
          {selectedCountLabel}
        </p>
      </div>
    </div>
  );
}


// MED-1172: active facility promotion (free trial month, or a rebate on the monthly price).
// Uses BFF data only — the FREE_TRIAL message is translated server-side (translatedMessage);
// a rebate promotion only carries the percentage, so the discounted/original price display
// is computed client-side. MED-1573: the server bills the rebate on the licence's
// monthlyAmount (already net of the duration discount), not on its baseAmount.
// MED-1584: the server now sends the discounted monthly amount it bills; it is shown as
// is, and the local calculation is only a fallback for a BFF that does not send it.
function PromotionSection({
  promotion, monthlyAmount, discountedMonthlyAmount, fmt, formatDate, t,
}: {
  promotion: ActivePromotionResponse;
  monthlyAmount: number;
  discountedMonthlyAmount?: number | null;
  fmt: (amount: number | undefined) => string;
  formatDate: (dateStr: string | null | undefined) => string;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const isFreeTrial = promotion.promotionCode === "FREE_TRIAL";
  const rebate = promotion.rebate ?? 0;
  const discountedAmount = discountedMonthlyAmount ?? applyPromotion(monthlyAmount, promotion).amount;

  return (
    <div className="flex items-start gap-3 p-4 border bg-success-50 border-success-200 rounded-xl text-success-800">
      <BadgeCheck className="flex-shrink-0 w-5 h-5 mt-0.5 text-success-600" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">{t("licence.promotion.title")}</p>
        <p className="mt-0.5 text-xs text-success-700">{promotion.translatedMessage}</p>

        {isFreeTrial ? (
          <div className="flex items-center gap-2 mt-2 text-xs">
            <span className="font-medium">{t("licence.promotion.freeTrial.nextPayment")}:</span>
            <span className="font-semibold">{fmt(0)}</span>
          </div>
        ) : (
          <div className="mt-2 space-y-1 text-xs">
            <div>
              <span className="font-medium">
                {t("licence.promotion.rebate.percentageLabel", { rebate })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">{t("licence.promotion.rebate.discountedPrice")}:</span>
              <span className="font-semibold">{fmt(discountedAmount)}</span>
              <span className="line-through text-success-600/70">{fmt(monthlyAmount)}</span>
            </div>
            <div>
              {t("licence.promotion.rebate.validity", {
                startDate: formatDate(promotion.startDate),
                endDate: formatDate(promotion.endDate),
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// MED-1573: the monthly total row of a price estimate. While a rebate promotion is active
// the estimate shows what the server will actually bill while it lasts: the contractual
// monthly amount (labelled as such), the promotion line with its end date, and the
// discounted total. The estimate does not try to project which future months fall in the
// promotion window — the promotion is applied for as long as it is active, and its end
// date is shown so the manager knows it is temporary. A FREE_TRIAL promotion (one offered
// period) and no promotion leave the row unchanged.
function MonthlyTotalRows({
  label, monthly, promotion, fmt, formatDate, t,
}: {
  label: string;
  monthly: number;
  promotion: ActivePromotionResponse | null;
  fmt: (amount: number | undefined) => string;
  formatDate: (dateStr: string | null | undefined) => string;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const { rebate, discount, amount } = applyPromotion(monthly, promotion);
  const totalRow = (rowLabel: string, value: number) => (
    <div className="flex justify-between pt-2 font-semibold border-t text-neutral-900 border-neutral-200">
      <span>{rowLabel}</span>
      <span className="text-lg text-primary-600">{fmt(value)}</span>
    </div>
  );
  if (!promotion || rebate === 0) return totalRow(label, monthly);
  return (
    <>
      <div className="flex justify-between text-sm text-neutral-600">
        <span>{t("licence.promotion.estimate.contractualMonthly")}</span>
        <span>{fmt(monthly)}</span>
      </div>
      <div className="flex justify-between text-sm text-success-600">
        <span>{t("licence.promotion.estimate.line", { rebate, endDate: formatDate(promotion.endDate) })}</span>
        <span>-{fmt(discount)}</span>
      </div>
      {totalRow(t("licence.promotion.estimate.totalWithPromotion"), amount)}
    </>
  );
}

function InfoCell({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-xs text-neutral-500">
        {icon}
        {label}
      </div>
      <p className="text-sm font-semibold text-neutral-900">{value}</p>
      {sub && <p className="text-xs text-warning-600">{sub}</p>}
    </div>
  );
}
